import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  useBookings,
  useBookingPayments,
  useCanEditBookingFinancials,
} from '../../../../hooks/accommodation';
import { useAppSelector } from '../../../../hooks/useRedux';
import { selectAccommodationProperty } from '../../../../store/accommodation';
import {
  ACCOMMODATION_PAYMENT_METHODS,
  AccommodationPayment,
} from '../../../../types/accommodation';
import { RecordPaymentModal } from './RecordPaymentModal';
import { DeletePaymentModal } from './DeletePaymentModal';
import { EditPaymentModal } from './EditPaymentModal';

export const BookingPaymentsCard: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const property = useAppSelector(selectAccommodationProperty);
  const { data: bookings = [] } = useBookings(property?.id);
  const booking = bookings.find((b) => b.id === bookingId) ?? null;

  const { data: payments = [] } = useBookingPayments(bookingId);
  const { canEdit: canEditPayments, hasPermission } =
    useCanEditBookingFinancials(booking);

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] =
    useState<AccommodationPayment | null>(null);
  const [deletingPayment, setDeletingPayment] =
    useState<AccommodationPayment | null>(null);

  if (!booking) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-3.5">
      <div className="flex items-center justify-between border-b border-gray-100 pb-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Payments
        </h4>
        {booking.status !== 'cancelled' &&
          booking.status !== 'checked_out' &&
          booking.status !== 'no_show' && (
            <button
              type="button"
              onClick={() => setIsPaymentModalOpen(true)}
              className="flex items-center gap-1 rounded-lg bg-pos-primary px-2.5 py-1 text-xs font-medium text-white hover:bg-pos-primary/90 transition-colors"
            >
              <Plus size={12} />
              Record
            </button>
          )}
      </div>

      {/* Balance summary */}
      {(() => {
        const balance = Math.max(
          0,
          booking.total_amount - (booking.amount_paid ?? 0),
        );
        return (
          <div
            className={[
              'flex items-center justify-between rounded-xl px-3 py-2 text-xs font-semibold',
              balance > 0
                ? 'bg-yellow-50 text-yellow-700'
                : 'bg-green-50 text-green-700',
            ].join(' ')}
          >
            <span>Balance due</span>
            <span>
              {booking.currency} {balance.toLocaleString()}
            </span>
          </div>
        );
      })()}

      {/* Payment list */}
      {payments.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 py-5 text-center bg-slate-50/50 rounded-xl border border-dashed border-gray-200">
          <CreditCard size={22} className="text-gray-300" />
          <p className="text-xs text-gray-400 font-medium">
            No payments recorded yet
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {payments.map((p) => {
            const method = ACCOMMODATION_PAYMENT_METHODS.find(
              (m) => m.id === p.payment_method_id,
            );
            return (
              <div
                key={p.id}
                className="flex items-center justify-between rounded-xl border border-gray-100 px-3.5 py-3 gap-2 bg-slate-50/50"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-gray-800">
                    {method?.name ?? `Method ${p.payment_method_id}`}
                  </p>
                  <p className="text-xs text-gray-400 font-medium mt-0.5">
                    {new Date(p.paid_at).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                    {p.notes ? ` · ${p.notes}` : ''}
                  </p>
                </div>
                <span className="text-xs font-bold text-emerald-600 shrink-0">
                  +{booking.currency} {p.amount.toLocaleString()}
                </span>
                {canEditPayments && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditingPayment(p)}
                      className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                      title="Edit payment"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeletingPayment(p)}
                      className="rounded-lg p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      title="Delete payment"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasPermission && payments.length > 0 && !canEditPayments ? (
        <p className="flex items-center gap-1.5 text-[11px] text-gray-400">
          <Lock size={11} />
          Payments can only be edited before check-in.
        </p>
      ) : null}

      <RecordPaymentModal
        isOpen={isPaymentModalOpen}
        booking={booking}
        onClose={() => setIsPaymentModalOpen(false)}
      />

      <EditPaymentModal
        isOpen={!!editingPayment}
        payment={editingPayment}
        booking={booking}
        onClose={() => setEditingPayment(null)}
      />

      <DeletePaymentModal
        isOpen={!!deletingPayment}
        payment={deletingPayment}
        booking={booking}
        onClose={() => setDeletingPayment(null)}
      />
    </div>
  );
};
